// src/features/records/components/RecordDetail/hooks/useCommentDeepLinkFocus.js
import { useMemo, useEffect, useRef, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

const normalizeFocusTarget = (target) => {
  if (!target) {
    return null;
  }
  const rawCommentId = typeof target.commentId === 'string' ? target.commentId.trim() : '';
  const rawThreadRootId = typeof target.threadRootId === 'string' ? target.threadRootId.trim() : '';
  const commentId = rawCommentId || rawThreadRootId;
  const threadRootId = rawThreadRootId || rawCommentId;
  if (!commentId || !threadRootId) {
    return null;
  }
  return { commentId, threadRootId };
};

const getFocusKey = (recordId, commentId) => {
  if (!recordId || !commentId) {
    return null;
  }
  return `${recordId}:${commentId}`;
};

const useCommentDeepLinkFocus = ({
  recordId,
  initialFocusTarget,
  rootComments,
  repliesByRootId,
  loadingRoots,
  loadingRepliesByRootId,
  expandedRoots,
  setExpandedRoots,
  loadReplies,
  ensureRootCommentLoaded,
  toast
}) => {
  const { t } = useTranslation();
  const [highlightedCommentId, setHighlightedCommentId] = useState(null);
  const highlightTimeoutRef = useRef(null);
  const didFocusRef = useRef(null);

  const normalizedFocusTarget = useMemo(
    () => normalizeFocusTarget(initialFocusTarget),
    [initialFocusTarget]
  );

  const triggerHighlight = useCallback((commentId) => {
    if (!commentId) return;
    setHighlightedCommentId(commentId);
    if (highlightTimeoutRef.current) {
      clearTimeout(highlightTimeoutRef.current);
    }
    highlightTimeoutRef.current = setTimeout(() => {
      setHighlightedCommentId((current) => (current === commentId ? null : current));
    }, 2000);
  }, []);

  useEffect(() => {
    didFocusRef.current = null;
    setHighlightedCommentId(null);
    if (highlightTimeoutRef.current) {
      clearTimeout(highlightTimeoutRef.current);
      highlightTimeoutRef.current = null;
    }
  }, [recordId]);

  useEffect(() => {
    return () => {
      if (highlightTimeoutRef.current) {
        clearTimeout(highlightTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!normalizedFocusTarget || !recordId) {
      return;
    }

    const { threadRootId, commentId } = normalizedFocusTarget;
    const focusKey = getFocusKey(recordId, commentId);

    if (!focusKey || didFocusRef.current === focusKey) {
      return;
    }

    if (loadingRoots) {
      return;
    }

    let cancelled = false;

    const markMissing = () => {
      if (didFocusRef.current === focusKey) {
        return;
      }
      toast.error(t('records.comments.deep_link_missing', 'Comment is no longer available.'));
      didFocusRef.current = focusKey;
    };

    const runFocus = async () => {
      const existingRoot = rootComments.find((comment) => comment?.id === threadRootId);
      if (!existingRoot) {
        const fetchedRoot = await ensureRootCommentLoaded(threadRootId);
        if (cancelled) {
          return;
        }
        if (!fetchedRoot) {
          markMissing();
        }
        return;
      }

      if (existingRoot.status === 'deleted') {
        markMissing();
        return;
      }

      if (commentId !== threadRootId) {
        const isExpanded = Boolean(expandedRoots[threadRootId]);
        if (!isExpanded) {
          setExpandedRoots((prev) => ({ ...prev, [threadRootId]: true }));
        }
        const hasLoadedReplies = Object.prototype.hasOwnProperty.call(
          repliesByRootId,
          threadRootId
        );
        if (!hasLoadedReplies && !loadingRepliesByRootId[threadRootId]) {
          await loadReplies(threadRootId);
          return;
        }
        if (!isExpanded || !hasLoadedReplies || loadingRepliesByRootId[threadRootId]) {
          return;
        }
      }

      const targetElement = document.getElementById(`record-comment-${commentId}`);

      if (!targetElement) {
        if (commentId !== threadRootId) {
          const replies = repliesByRootId[threadRootId] || [];
          const replyExists = replies.some((reply) => reply?.id === commentId);
          if (!replyExists) {
            markMissing();
          }
        } else {
          markMissing();
        }
        return;
      }

      targetElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      triggerHighlight(commentId);
      didFocusRef.current = focusKey;
    };

    runFocus();

    return () => {
      cancelled = true;
    };
  }, [
    normalizedFocusTarget,
    recordId,
    rootComments,
    repliesByRootId,
    loadingRoots,
    loadingRepliesByRootId,
    expandedRoots,
    loadReplies,
    ensureRootCommentLoaded,
    setExpandedRoots,
    toast,
    t,
    triggerHighlight
  ]);

  return {
    highlightedCommentId
  };
};

export default useCommentDeepLinkFocus;
